import React, { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import './HomeCarouselFigma.css';

/**
 * Figma node 2374:2 - "A connected system for the full real estate market"
 * Scroll-pinned horizontal carousel (Framer "Scroll Carousel" pattern).
 *  - Section is N slides × SLIDE_SCROLL_VH tall.
 *  - Inner pin sticks at top:0; a flex track translates by (slide-w + gap) per step.
 *  - Non-active slides get dim + blur + scale so they look "glass" at the edges.
 *
 * Videos stay muted / looping / no controls. Playback starts from JS only
 * when the section is near the viewport so the landing page does not fetch
 * all five clips during first paint.
 */
const SLIDES = [
  {
    title: ['A connected system', 'for the full real estate market'],
    sub:   ['One subscription. One System. The full experience.'],
    video: '/landing-carousel/slide-video.mp4',
    poster: '/landing-carousel/slide-1.jpg',
  },
  {
    title: ['Global property access,', 'connected in one system'],
    sub:   ['Discover opportunities, markets,', 'and cross-border activity with greater ease.'],
    video: '/landing-carousel/slide-video-2.mp4',
    poster: '/landing-carousel/slide-2.jpg',
  },
  {
    title: ['Real-time insight', 'for more informed decisions'],
    sub:   ['Live property and market data helps every stakeholder', 'move with greater confidence.'],
    video: '/landing-carousel/slide-video-3.mp4',
    poster: '/landing-carousel/slide-3.jpg',
  },
  {
    title: ['Clear visibility across', 'every stage of the process'],
    sub:   ['Track deals, documents, updates,', 'and next steps from start to finish.'],
    video: '/landing-carousel/slide-video-4.mp4',
    poster: '/landing-carousel/slide-4.jpg',
  },
  {
    title: ['From first search', 'to final transfer and beyond'],
    sub:   ['IPM supports the full property journey', 'in one connected platform.'],
    video: '/landing-carousel/slide-video-5.mp4',
    poster: '/landing-carousel/slide-5.jpg',
  },
];

// Viewport heights of scroll per slide transition (bigger = more gradual).
// 80vh per slide × 4 transitions = 320vh of pinned scrolling + 100vh dwell
// = 420vh section height. Previously 150vh per slide made the section 700vh
// tall, so on most viewports users scrolled past the pin before reaching the
// later slides and only ever saw slide 1.
const SLIDE_SCROLL_VH = 80;

function CarouselBgVideo({ src, poster, play }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (!play) {
      el.pause();
      return undefined;
    }

    if (el.getAttribute('src') !== src) {
      el.src = src;
    }

    const tryPlay = () => {
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    if (el.readyState >= 3) {
      tryPlay();
      return undefined;
    }

    el.addEventListener('canplay', tryPlay);
    el.load();
    return () => el.removeEventListener('canplay', tryPlay);
  }, [play, src]);

  return (
    <video
      ref={ref}
      className="home-carousel__bg"
      poster={poster}
      muted
      loop
      playsInline
      preload={play ? 'auto' : 'none'}
      disablePictureInPicture
      aria-hidden="true"
    />
  );
}

export default function HomeCarouselFigma() {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const isCompact = useMediaQuery('(max-width: 900px)');
  const [progress, setProgress] = useState(0);
  const [mobileIndex, setMobileIndex] = useState(0);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setInView(true);
      },
      { rootMargin: '240px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (isCompact) {
      setProgress(0);
      return undefined;
    }
    let rafId = 0;
    const compute = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      if (total <= 0) {
        setProgress(0);
        return;
      }
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      setProgress(scrolled / total);
    };
    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        compute();
      });
    };
    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [isCompact]);

  useEffect(() => {
    if (!isCompact) return undefined;
    const pin = pinRef.current;
    if (!pin) return undefined;

    const syncIndex = () => {
      const firstSlide = pin.querySelector('.home-carousel__slide');
      if (!firstSlide) return;
      const styles = window.getComputedStyle(pin.querySelector('.home-carousel__track'));
      const gap = parseFloat(styles.columnGap || styles.gap || '12') || 12;
      const slideW = firstSlide.getBoundingClientRect().width;
      const step = slideW + gap;
      if (step <= 0) return;
      const idx = Math.round(pin.scrollLeft / step);
      setMobileIndex(Math.min(Math.max(idx, 0), SLIDES.length - 1));
    };

    syncIndex();
    pin.addEventListener('scroll', syncIndex, { passive: true });
    window.addEventListener('resize', syncIndex);
    return () => {
      pin.removeEventListener('scroll', syncIndex);
      window.removeEventListener('resize', syncIndex);
    };
  }, [isCompact]);

  const activeFloat = progress * (SLIDES.length - 1);
  const activeIndex = isCompact ? mobileIndex : Math.round(activeFloat);

  // Shift the track by (slide-w + gap) for each slide we've scrolled past.
  const trackTransform = isCompact
    ? undefined
    : `translate3d(calc(${-activeFloat} * (var(--slide-w) + var(--slide-gap))), 0, 0)`;

  return (
    <section
      ref={sectionRef}
      className={`home-carousel${isCompact ? ' home-carousel--compact' : ''}`}
      aria-label="A connected system for the full real estate market"
      style={{
        '--carousel-slides': SLIDES.length,
        '--carousel-scroll-vh': SLIDE_SCROLL_VH,
      }}
    >
      <div className="home-carousel__pin" ref={pinRef}>
        <div
          className="home-carousel__track"
          style={trackTransform ? { transform: trackTransform } : undefined}
        >
          {SLIDES.map((slide, i) => {
            // The nearest slide to the scroll position stays fully crisp; only
            // slides more than ±0.5 away start to glass up.
            const distance = Math.abs(i - (isCompact ? mobileIndex : activeFloat));
            const glass = isCompact ? 0 : Math.min(Math.max(0, distance - 0.5), 1.5);
            const opacity = isCompact ? 1 : Math.max(0.35, 1 - glass * 0.6);
            const blurPx = isCompact ? 0 : glass * 8;
            const scale = isCompact ? 1 : 1 - glass * 0.08;
            const style = isCompact
              ? undefined
              : {
                  opacity,
                  filter: `blur(${blurPx}px)`,
                  transform: `scale(${scale})`,
                };
            const play = inView && Math.abs(i - activeIndex) <= 1;
            return (
              <article
                className="home-carousel__slide"
                key={i}
                aria-hidden={i !== activeIndex}
                style={style}
              >
                <div className="home-carousel__card">
                  <CarouselBgVideo src={slide.video} poster={slide.poster} play={play} />
                  <div className="home-carousel__scrim" aria-hidden />
                  <div className="home-carousel__copy">
                    <h2 className="home-carousel__title">
                      {slide.title.map((line, j) => (
                        <span key={j} className="home-carousel__title-line">
                          {line}
                        </span>
                      ))}
                    </h2>
                    <p className="home-carousel__sub">
                      {slide.sub.map((line, j) => (
                        <span key={j} className="home-carousel__sub-line">
                          {line}
                        </span>
                      ))}
                    </p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <div className="home-carousel__dots" role="tablist" aria-label="Slide indicator">
          {SLIDES.map((_, i) => (
            <span
              key={i}
              className={`home-carousel__dot ${i === activeIndex ? 'is-active' : ''}`}
              aria-label={`Slide ${i + 1} of ${SLIDES.length}`}
              role="tab"
              aria-selected={i === activeIndex}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
